import React, { useState, useEffect } from "react";
import { Menu, X, Download, Apple } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";
import { trackEvent } from "../lib/analytics";

const PLAY_URL = "https://play.google.com/store/apps/details?id=fi.duunari.app";
const APPSTORE_URL = "https://apps.apple.com/app/duunari-app/id6779275969";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, lang } = useLanguage();

  const NAV_LINKS = [
    { label: t("nav.features"), href: "#ominaisuudet" },
    { label: t("nav.workflow"), href: "#tyonkulku" },
    { label: t("nav.pricing"), href: "#hinnasto" },
    { label: t("nav.faq"), href: "#ukk" },
    { label: t("nav.contact"), href: "#yhteystiedot" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0F0F0F]/95 backdrop-blur-md border-b-2 border-[#1f1f1f]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        {/* Logo */}
        <a
          href="#"
          data-testid="logo-link"
          className="flex items-center gap-3 group"
        >
          <Logo size={44} aura testId="header-logo" />
          <div className="flex flex-col leading-none">
            <span className="font-display font-bold text-white text-xl tracking-wider">
              DUUNARI
            </span>
            <span className="font-mono text-[9px] tracking-[0.25em] text-[#FFEA00]">
              v1.0 / {lang.toUpperCase()}
            </span>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className="font-display uppercase text-xs lg:text-sm tracking-widest text-[#A3A3A3] hover:text-[#FFEA00] transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 h-[2px] w-0 bg-[#FFEA00] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        {/* Desktop CTA — lang switcher + dual store buttons */}
        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher variant="desktop" />
          <a
            href={PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-play-button"
            onClick={() => trackEvent("Click: Google Play")}
            className="btn-hazard inline-flex items-center gap-2 bg-[#FFEA00] text-black font-display font-bold uppercase text-xs tracking-widest px-4 py-2.5 border-2 border-[#FFEA00] hard-shadow"
          >
            <Download className="w-4 h-4 stroke-[2.5]" />
            Google Play
          </a>
          <a
            href={APPSTORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-appstore-button"
            onClick={() => trackEvent("Click: App Store")}
            className="btn-hazard inline-flex items-center gap-2 bg-transparent text-[#FFEA00] font-display font-bold uppercase text-xs tracking-widest px-4 py-2.5 border-2 border-[#FFEA00] hard-shadow-white"
          >
            <Apple className="w-4 h-4 stroke-[2]" />
            App Store
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          className="md:hidden w-11 h-11 flex items-center justify-center border-2 border-[#333] text-white hover:border-[#FFEA00] hover:text-[#FFEA00] transition-colors"
          aria-label="Avaa valikko"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile nav */}
      {open && (
        <div
          className="md:hidden bg-[#0F0F0F] border-t-2 border-[#1f1f1f]"
          data-testid="mobile-menu"
        >
          <div className="px-5 py-5 flex flex-col gap-1">
            {/* Mobile language switcher */}
            <div className="mb-3">
              <LanguageSwitcher variant="mobile" />
            </div>

            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                className="font-display uppercase text-sm tracking-widest text-white hover:text-[#FFEA00] py-3 border-b border-[#1f1f1f]"
              >
                {l.label}
              </a>
            ))}

            {/* Mobile store buttons — stacked */}
            <div className="mt-5 flex flex-col gap-2.5">
              <a
                href={PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-play-button"
                onClick={() => { trackEvent("Click: Google Play"); setOpen(false); }}
                className="btn-hazard inline-flex items-center justify-center gap-2 bg-[#FFEA00] text-black font-display font-bold uppercase text-sm tracking-widest px-5 py-3.5 border-2 border-[#FFEA00]"
              >
                <Download className="w-4 h-4 stroke-[2.5]" />
                Google Play
              </a>
              <a
                href={APPSTORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-appstore-button"
                onClick={() => { trackEvent("Click: App Store"); setOpen(false); }}
                className="btn-hazard inline-flex items-center justify-center gap-2 bg-transparent text-[#FFEA00] font-display font-bold uppercase text-sm tracking-widest px-5 py-3.5 border-2 border-[#FFEA00]"
              >
                <Apple className="w-4 h-4 stroke-[2]" />
                App Store
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Hazard bottom edge */}
      <div className="h-[3px] hazard-stripes-anim opacity-90" />
    </header>
  );
};
